import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { Link } from "react-router-dom";

import { HubConnectionBuilder } from "@microsoft/signalr";

import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Table from "react-bootstrap/Table";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import { toast } from 'react-toastify';

import authorizeService from "./api-authorization/AuthorizeService";
import reviewService from "../services/ReviewService";
import { secondsToTime, round2Decimal } from "../utilities/scoringUtilities";

import SupremacyGraph from "../components/Review/SupremacyGraph";
import BreakdownGraph from "../components/Review/BreakdownGraph";
import { ScoringInputType } from "../models/enums/ScoringInputType";
import liveRoomService from "../services/LiveRoomService";
import ReviewNoteModal from "./Modals/ReviewNoteModal";
import { ApplicationRoles } from './api-authorization/ApiAuthorizationConstants';

export const Review = (props) => {
    let history = useHistory();
    const {fixtureRoomId} = useParams();

    const [reviewDetails, setReviewDetails] = useState(null);
    const [inputList, setInputList] = useState(null);
    const [supremacyChart, setSupremacyChart] = useState(null);
    const [breakdown, setBreakdown] = useState(null);
    const [isRoomActive, setIsRoomActive] = useState(null);
    const [loading, setLoading] = useState(true);

    const [connection, setConnection] = useState(null);

    const [showNoteModal, setShowNoteModal] = useState(false);
    const [clipList, setClipList] = useState();
    const [clipOverview, setClipOverview] = useState(null);
    const [clipReviewed, setClipReviewed] = useState([]);
    const [noteDetail, setNoteDetail] = useState([]);
    const [userRole, setUserRole] = useState(null);
    const [activeUserId, setActiveUserId] = useState(null);

    useEffect(() => {
        if (!userRole) {
            authorizeService.getUser().then((user) => {
                setActiveUserId(user.sub);
                setUserRole(user.role);
            })
        }
    }, []);

    useEffect(() => {
        if (!reviewDetails) {
            reviewService.getReviewDetails(fixtureRoomId).then((result) => {
                setReviewDetails(result);
                setIsRoomActive(result.roomActive);
                setLoading(false);

            });
        }
    }, [reviewDetails]);

    useEffect(() => {
        if (!inputList) {
            reviewService.getReviewInputList(fixtureRoomId).then((result) => {
                setInputList(result);
            });
        }
    }, [inputList]);
    useEffect(() => {
        if (!supremacyChart) {
            reviewService.getSupremacyChart(fixtureRoomId).then((result) => {
                setSupremacyChart(result);
            });
        }
    }, [supremacyChart]);

    useEffect(() => {
        if (!breakdown) {
            reviewService.getBreakdown(fixtureRoomId).then((result) => {
                setBreakdown(result);
            });
        }
    }, [breakdown]);

    useEffect(() => {
        if (!clipOverview) {
            reviewService.getReviewClipOverview(fixtureRoomId).then((result) => {
                setClipOverview(result);
                setClipReviewed(result.inputList.map(x => ({id: x.clipId, key: x.isReviewed})));
                setNoteDetail(result.inputList.map(x => ({id: x.clipId, key: x.notes})));
            });
        }
    }, [clipOverview]);

    useEffect(() => {
        let getUrl = window.location;
        let baseUrl = getUrl.protocol + "//" + getUrl.host;

        const connection = new HubConnectionBuilder()
            .withUrl(`${baseUrl}/hubs/fixturerooms`, {
                accessTokenFactory: () => authorizeService.getAccessToken(),
            })
            .withAutomaticReconnect()
            .build();

        setConnection(connection);
    }, []);

    useEffect(() => {
        if (connection) {
            connection
                .start()
                .then((res) => {
                    connection.invoke("JoinRoomReview", fixtureRoomId).catch((err) => {
                        console.log(err);
                    });
                })
                .catch((err) => {
                    console.log(err);
                })
                .then((result) => {
                    console.log("Connected!");

                    connection.on("ReviewDetailsUpdate", (message) => {
                        setReviewDetails(message);
                    });

                    connection.on("ReviewSupremacyChartUpdate", (message) => {
                        setSupremacyChart(message);
                    });

                    connection.on("ReviewInputListUpdate", (message) => {
                        setInputList(message);
                    });

                    connection.on("ReviewBreakdownUpdate", (message) => {
                        setBreakdown(message);
                    });
                })
                .catch((e) => console.log("Connection failed: ", e));
        }
    }, [connection]);
    const hideReviewNoteModal = () => {
        setShowNoteModal(false);
    };

    const showReviewNoteModal = () => {
        setShowNoteModal(true);
    };

    const reviewNoteModal = (
        <ReviewNoteModal
            onHide={hideReviewNoteModal}
            show={showNoteModal}
            data={clipList}
            noteDetail={noteDetail}
            setNoteDetail={setNoteDetail}
        ></ReviewNoteModal>
    );

    const isReviewedOnChangeHandle = (clipId, isReview) => {
        reviewService.setClipReviewed(clipId, !isReview);
        let updatedList = clipReviewed.map(item => {
            if (item.id === clipId) {
                return {...item, key: !item.key}
            }
            return item;
        });
        setClipReviewed(updatedList);
    }

    const renderTable = (inputList) => {
        const content =
            !inputList || inputList.length == 0 ? (
                <tr className="p-2">
                    <td colSpan="19" className="text-center">
                        Nothing to review
                    </td>
                </tr>
            ) : (
                inputList.map((x, index) => {
                    let key = x.id + "_" + x.type;
                    let scoreClass =
                        x.type == 5 || x.type == 11 || x.type == 13
                            ? x.score >= 0
                                ? "text-success"
                                : "text-danger"
                            : "";
                    let calcScoreClass =
                        x.type == 5 || x.type == 11 || x.type == 13
                            ? x.score >= 0
                                ? "H"
                                : "A"
                            : "";
                    let previousSupremacyIndex = index - 1 < 0 ? 0 : index - 1;
                    let previousSupremacyPoint = getSupremacyPoint(
                        inputList[previousSupremacyIndex].type,
                        inputList[previousSupremacyIndex].clipStartSupremacy,
                        inputList[previousSupremacyIndex].clipEndSupremacy,
                        inputList[previousSupremacyIndex].actionSupremacy
                    );
                    let calculatedPoint =
                        previousSupremacyPoint == ""
                            ? ""
                            : getSupremacyPoint(
                            x.type,
                            x.clipStartSupremacy,
                            x.clipEndSupremacy,
                            x.actionSupremacy
                        ) - previousSupremacyPoint;

                    let rowClass = x.type == 4 ? "table-info" : "";

                    return (
                        <tr key={key} className={rowClass}>
                            <td>{++index}</td>
                            <td>{x.half}</td>
                            <td>{x.clipNumber}</td>
                            <td>{secondsToTime(x.time)}</td>
                            <td>{x.typeText}</td>
                            <td>
                                {x.type == 5 && (
                                    <>
                    <span>
                      {x.teamAbbreviation}
                        {x.playerShirtNumber}{" "}
                    </span>
                                        <span className={scoreClass}>{x.scoreText} </span>
                                        <span>{x.categorizationsText}</span>
                                        <span>
                      {" "}
                                            {x.teamDescriptionsText}
                                            {x.playerDescriptionsText}
                    </span>
                                    </>
                                )}

                                {x.type == 3 && (
                                    <>
                                        <span>{x.clipStartText}</span>
                                    </>
                                )}

                                {x.type == 4 && (
                                    <>
                                        <span>{x.clipEndText}</span>
                                    </>
                                )}

                                {(x.type == 9 || x.type == 8) && (
                                    <>
                                        <span>{x.cardScoreText}</span>
                                    </>
                                )}

                                {x.type == 11 && (
                                    <>
                                        <span>{x.teamAbbreviation} </span>
                                        <span className={scoreClass}>{x.scoreText} </span>
                                        <span>{x.categorizationsText}</span>
                                    </>
                                )}

                                {x.type == 10 && (
                                    <>
                                        <span>{x.midClipText}</span>
                                    </>
                                )}

                                {x.type == 13 && (
                                    <>
                                        <span>{x.tryText}</span>
                                    </>
                                )}
                            </td>
                            <td>{x.teamName}</td>
                            <td>{x.playerShirtNumber}</td>
                            <td>
                                {x.type == 5 && (
                                    <>
                                        <span>{x.playerScoreText}</span>
                                    </>
                                )}

                                {x.type == 3 && (
                                    <>
                                        <span>{x.clipStartPositionText}</span>
                                    </>
                                )}

                                {x.type == 4 && (
                                    <>
                                        <span>{x.clipEndPositionText}</span>
                                    </>
                                )}

                                {x.type == 11 && (
                                    <>
                                        <span>{x.teamScoreText}</span>
                                    </>
                                )}

                                {x.type == 13 && (
                                    <>
                                        <span>{x.tryPositionText}</span>
                                    </>
                                )}
                            </td>
                            <td>
                                {(x.type == 5 || x.type == 11 || x.type == 13) && (
                                    <span className={scoreClass}>{x.scoreText} </span>
                                )}
                            </td>
                            <td>
                <span className={calcScoreClass}>
                  {calculatedPoint
                      ? x.scoreText
                          ? Math.round(calculatedPoint * 1000) / 1000
                          : ""
                      : ""}{" "}
                </span>
                            </td>
                            <td>
                                {x.clipStartSupremacy
                                    ? Math.round(x.clipStartSupremacy * 1000) / 1000
                                    : ""}
                            </td>
                            <td>
                                {x.clipEndSupremacy
                                    ? Math.round(x.clipEndSupremacy * 1000) / 1000
                                    : ""}
                            </td>
                            <td>
                <span className={calcScoreClass}>
                  {x.type == 10
                      ? inputList.some((x) => x.type === ScoringInputType.MidClip)
                          ? Math.round(x.calculatedPoint * 1000) / 1000
                          : ""
                      : ""}
                </span>
                            </td>
                            <td>
                                {x.type == 3
                                    ? x.clipStartSupremacy
                                        ? Math.round(x.clipStartSupremacy * 1000) / 1000
                                        : ""
                                    : x.type == 4
                                        ? x.clipEndSupremacy
                                            ? Math.round(x.clipEndSupremacy * 1000) / 1000
                                            : ""
                                        : x.type == 5 || x.type == 11 || x.type == 13
                                            ? x.actionSupremacy
                                                ? Math.round(x.actionSupremacy * 1000) / 1000
                                                : ""
                                            : ""}
                            </td>
                            <td>{x.scoreInClip}</td>
                            <td>{x.cumulativeHomePoints}</td>
                            <td>{x.cumulativeAwayPoints}</td>
                            <td>
                                {x.type == 4 && (
                                    <span className="font-weight-bold text-success">
                    {x.clipHomePostiveRawScore}
                  </span>
                                )}
                            </td>
                            <td>
                                {x.type == 4 && (
                                    <span className="font-weight-bold text-danger">
                    {x.clipHomeNegativeRawScore}
                  </span>
                                )}
                            </td>
                            <td>
                                {x.type == 4 && (
                                    <span className="font-weight-bold text-success">
                    {x.clipAwayPostiveRawScore}
                  </span>
                                )}
                            </td>
                            <td>
                                {x.type == 4 && (
                                    <span className="font-weight-bold text-danger">
                    {x.clipAwayNegativeRawScore}
                  </span>
                                )}
                            </td>
                            <td>{x.isFlagged ? "Yes" : "No"}</td>
                            <td>
                                {x.isFlagged
                                    ? [
                                        <tr>
                                            {" "}
                                            {x.flagReasons.map((y) => (
                                                <tr> {y.text} </tr>
                                            ))}{" "}
                                        </tr>,
                                    ]
                                    : ""}
                            </td>
                            <td>{x.isFlagged ? x.flagNotes : ""}</td>
                        </tr>
                    );
                })
            );

        return (
            <>
                <div
                    style={{
                        overflowY: "scroll",
                        overflowX: "scroll",
                        whiteSpace: "nowrap",
                        maxHeight: "calc(100vh - 240px)",
                    }}
                >
                    <Table bordered hover className="react-bootstrap-table">
                        <thead>
                        <tr>
                            <th>#</th>
                            <th>Half</th>
                            <th>Clip #</th>
                            <th>Time</th>
                            <th>Action Type</th>
                            <th>Details</th>
                            <th>Team</th>
                            <th>Player Position</th>
                            <th>XY</th>
                            <th>Raw Score</th>
                            <th>Calc Score</th>
                            <th>Clip Start Supremacy</th>
                            <th>Clip End Supremacy</th>
                            <th>Adj</th>
                            <th>Supremacy</th>
                            <th>Score in clip</th>
                            <th>Home Points</th>
                            <th>Away Points</th>
                            <th>Home + Clip Score</th>
                            <th>Home - Clip Score</th>
                            <th>Away + Clip Score</th>
                            <th>Away - Clip Score</th>
                            <th>Flagged?</th>
                            <th>Flag Reason</th>
                            <th>Flag Notes</th>
                        </tr>
                        </thead>
                        <tbody>{content}</tbody>
                    </Table>
                </div>
            </>
        );
    };
    const clipOverviewTable = (clipOverview) => {
        const content =
            !clipOverview || clipOverview.length == 0 ? (
                <tr className="p-2">
                    <td colSpan="19" className="text-center">
                        Nothing to review
                    </td>
                </tr>
            ) : (
                clipOverview.map((x, index) => {
                    let key = x.id + "_" + x.type;
                    let rowClass = x.type == 4 ? "table-info" : "";
                    return (
                        <tr key={key} className={rowClass}>
                            <td>{x.clip}</td>
                            <td>{x.clipNumber}</td>
                            <td>{x.half}</td>
                            <td>
                                {secondsToTime(x.clipStartsAt) != 0
                                    ? secondsToTime(x.clipStartsAt)
                                    : "-"}
                            </td>
                            <td>
                                {secondsToTime(x.clipEndsAt) != 0
                                    ? secondsToTime(x.clipEndsAt)
                                    : "-"}
                            </td>
                            <td>
                                {x.clipStartSupremacy != 0
                                    ? x.clipStartSupremacy
                                    : "-"}
                            </td>
                            <td>
                                {x.clipEndSupremacy != 0
                                    ? x.clipEndSupremacy
                                    : "-"}
                            </td>
                            <td>
                                {round2Decimal(x.maxSupremacyHome) != 0
                                    ? round2Decimal(x.maxSupremacyHome)
                                    : "-"}
                            </td>
                            <td>
                                {round2Decimal(x.maxSupremacyAway) != 0
                                    ? round2Decimal(x.maxSupremacyAway)
                                    : "-"}
                            </td>
                            <td>
                                {round2Decimal(x.swingInSupremacy) != 0
                                    ? round2Decimal(x.swingInSupremacy)
                                    : "-"}
                            </td>
                            <td>
                                {x.swingRank != 0
                                    ? x.swingRank
                                    : "-"}
                            </td>
                            <td>
                                {round2Decimal(x.kPositiveHome) != 0
                                    ? round2Decimal(x.kPositiveHome)
                                    : "-"}
                            </td>
                            <td>
                                {round2Decimal(x.kNegativeAway) != 0
                                    ? round2Decimal(x.kNegativeAway)
                                    : "-"}
                            </td>
                            <td>
                                <input
                                    type={"checkbox"}
                                    checked={
                                        x.Problem
                                    }
                                ></input>
                            </td>
                            <td>
                                <input
                                    title={x.isReviewed === false ? x.reviewer + " " + new Date().toLocaleString() : x.reviewer}
                                    type={"checkbox"} checked={clipReviewed?.filter(y => y.id === x.clipId)[0]?.key}
                                    onChange={() => isReviewedOnChangeHandle(x.clipId, x.isReviewed)}>

                                </input>
                            </td>
                            <td
                                style={{cursor: "pointer"}}
                                onClick={() => {
                                    showReviewNoteModal();
                                    setClipList({...x, clipId: x.clipId, notes: x.notes});
                                }}
                            >
                                {Array.from(noteDetail)?.filter(y => y.id === x.clipId)[0]?.key ? <Link>Note</Link> :
                                    <Link>+</Link>}
                            </td>
                            <td>
                                <Link
                                    to={`/scoring/${reviewDetails.fixtureRoomId}?time=${x.clipStartsAt}`}
                                >
                                    Room link
                                </Link>
                            </td>
                            <td>
                                <a href={x.details ? x.details : "#"}>{x.clipNumber}</a>
                            </td>
                        </tr>
                    );
                })
            );

        return (
            <>
                <div
                    style={{
                        overflowY: "scroll",
                        overflowX: "scroll",
                        whiteSpace: "nowrap",
                        maxHeight: "calc(100vh - 240px)",
                    }}
                >
                    <Table bordered hover className="react-bootstrap-table">
                        <thead>
                        <tr style={{position: "sticky", top: 0, textAlign: 'center'}}>
                            <th colSpan={2}>Clip</th>
                            <th colSpan={3} style={{color: "red"}}>
                                Time
                            </th>
                            <th colSpan={2} style={{color: "red"}}>
                                Supremacy
                            </th>
                            <th colSpan={2} style={{color: "red"}}>
                                Max Supremacy
                            </th>
                            <th colSpan={9}></th>
                        </tr>
                        <tr style={{position: "sticky", top: 45, textAlign: 'center'}}>
                            <th>Clip</th>
                            <th>Midclip</th>
                            <th>Half</th>
                            <th>Clip Start</th>
                            <th>Clip End</th>

                            <th>Clip Start</th>
                            <th>Clip End</th>
                            <th>Home</th>
                            <th>Away</th>

                            <th>Swing in Supremacy</th>
                            <th>Swing in Supremacy (Rank)</th>
                            <th>K+ (Home)</th>
                            <th>K- (Away)</th>
                            <th>Problem?</th>
                            <th>Reviewed?</th>
                            <th>Notes</th>
                            <th>Link to clip</th>
                            <th>Details</th>
                        </tr>
                        </thead>
                        <tbody>{content}</tbody>
                    </Table>
                </div>
            </>
        );
    };

    const getHighlighted = (ids) => {
        let matches = inputList.inputList.filter((x) => ids.includes(x.id));
        return matches;
    };

    const renderGraph = (supremacyChart) => {
        if (!supremacyChart) {
            return <></>;
        }

        return (
            <SupremacyGraph
                labels={supremacyChart.labels}
                datapoints={supremacyChart.datapoints}
                dataIds={supremacyChart.ids}
                getHighlighted={getHighlighted}
            ></SupremacyGraph>
        );
    };

    const renderBreakdown = (breakdown) => {
        if (!breakdown) {
            return <></>;
        }

        let topPlayersRows =
            !breakdown.topPlayers || breakdown.topPlayers.length == 0 ? (
                <tr className="p-2">
                    <td colSpan="5" className="text-center">
                        Nothing to display
                    </td>
                </tr>
            ) : (
                breakdown.topPlayers.map((x, index) => {
                    let key = x.position;

                    return (
                        <tr key={key}>
                            <td>{++index}</td>
                            <td>{x.position}</td>
                            <td>{x.playerName}</td>
                            <td>{x.teamName}</td>
                            <td>{x.score}</td>
                        </tr>
                    );
                })
            );

        let categorizationRows =
            !breakdown.categoryBreakdown ||
            breakdown.categoryBreakdown.length == 0 ? (
                <tr className="p-2">
                    <td colSpan="4" className="text-center">
                        Nothing to display
                    </td>
                </tr>
            ) : (
                breakdown.categoryBreakdown.map((x, index) => {
                    let key = x.name;

                    return (
                        <tr key={key}>
                            <td>{x.homeTeamScore}</td>
                            <td>{x.name}</td>
                            <td>{x.awayTeamScore}</td>
                        </tr>
                    );
                })
            );

        let importantActionRows =
            !breakdown.importantActions || breakdown.importantActions.length == 0 ? (
                <tr className="p-2">
                    <td colSpan="6" className="text-center">
                        Nothing to display
                    </td>
                </tr>
            ) : (
                breakdown.importantActions.map((x, index) => {
                    let key = x.position;

                    return (
                        <tr key={key}>
                            <td>{++index}</td>
                            <td>{secondsToTime(x.time)}</td>
                            <td>{x.playerName}</td>
                            <td>{x.teamName}</td>
                            <td>{x.score}</td>
                            <td>{x.category}</td>
                        </tr>
                    );
                })
            );

        if (breakdown.categoryBreakdown && breakdown.categoryBreakdown.length > 0) {
            categorizationRows.push(
                <tr key={"overall"}>
                    <td>{breakdown.overallHomeTeamScore}</td>
                    <td>Overall</td>
                    <td>{breakdown.overallAwayTeamScore}</td>
                </tr>
            );
        }

        let breakdownLabels = breakdown.categoryBreakdown.map((x) => x.name);
        let homeTeamBreakdownDatapoints = breakdown.categoryBreakdown.map(
            (x) => x.homeTeamScore
        );
        let awayTeamBreakdownDatapoints = breakdown.categoryBreakdown.map(
            (x) => x.awayTeamScore
        );

        return (
            <div className="w-100 px-2">
                <h5 className="px-2 text-center rounded"
                    style={{backgroundColor: "#f2f2f2", border: "1px solid #dee2e6"}}>
                    <strong>Home Team</strong>
                    <div>{reviewDetails.homeTeam.name}</div>
                </h5>
                <Row className="mb-4 px-2">
                    <Col>
                        <div>
                            <strong>Points</strong>
                        </div>
                        <div>{breakdown.watcherHomeTeamPoints}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Tries</strong>
                        </div>
                        <div>{breakdown.homeTeamWatcherTries}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Conversions</strong>
                        </div>
                        <div>{breakdown.homeTeamWatcherConversions}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Penalties</strong>
                        </div>
                        <div>{breakdown.homeTeamWatcherPenalities}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Action Count</strong>
                        </div>
                        <div>{breakdown.homeTeamActionCount}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Positive Total</strong>
                        </div>
                        <div>
                          <span className="text-success">
                            {breakdown.homeTeamWatcherPositiveTotal}
                          </span>
                        </div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Negative Total</strong>
                        </div>
                        <div>
                          <span className="text-danger">
                            {breakdown.homeTeamWatcherNegativeTotal}
                          </span>
                        </div>
                    </Col>
                </Row>
                <h5 className="px-2 text-center rounded"
                    style={{backgroundColor: "#f2f2f2", border: "1px solid #dee2e6"}}>
                    <strong>Away Team</strong>
                    <div>{reviewDetails.awayTeam.name}</div>
                </h5>
                <Row className="mb-4 px-2">
                    <Col>
                        <div>
                            <strong>Points</strong>
                        </div>
                        <div>{breakdown.watcherAwayTeamPoints}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Tries</strong>
                        </div>
                        <div>{breakdown.awayTeamWatcherTries}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Conversions</strong>
                        </div>
                        <div>{breakdown.awayTeamWatcherConversions}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Penalties</strong>
                        </div>
                        <div>{breakdown.awayTeamWatcherPenalties}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Action Count</strong>
                        </div>
                        <div>{breakdown.awayTeamActionCount}</div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Positive Total</strong>
                        </div>
                        <div>
                          <span className="text-success">
                            {breakdown.awayTeamWatcherPositiveTotal}
                          </span>
                        </div>
                    </Col>
                    <Col>
                        <div>
                            <strong>Negative Total</strong>
                        </div>
                        <div>
                          <span className="text-danger">
                            {breakdown.awayTeamWatcherNegativeTotal}
                          </span>
                        </div>
                    </Col>
                </Row>
                <hr></hr>
                <Row></Row>
                <Row>
                    <Col>
                        <h4>Important actions</h4>
                        <Table striped bordered hover className="">
                            <thead>
                            <tr>
                                <th>#</th>
                                <th>Time</th>
                                <th>Player</th>
                                <th>Team</th>
                                <th>Score</th>
                                <th>Category</th>
                            </tr>
                            </thead>
                            <tbody>{importantActionRows}</tbody>
                        </Table>
                    </Col>
                    <Col>
                        <h4>Top players</h4>
                        <Table striped bordered hover className="">
                            <thead>
                            <tr>
                                <th>#</th>
                                <th>Position</th>
                                <th>Player</th>
                                <th>Team</th>
                                <th>Score</th>
                            </tr>
                            </thead>
                            <tbody>{topPlayersRows}</tbody>
                        </Table>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <h4>Categorization totals</h4>
                        <Table striped bordered hover className="">
                            <thead>
                            <tr>
                                <th>Home</th>
                                <th>Category</th>
                                <th>Away</th>
                            </tr>
                            </thead>
                            <tbody>{categorizationRows}</tbody>
                        </Table>
                    </Col>
                    <Col>
                        <BreakdownGraph
                            labels={breakdownLabels}
                            homeTeamDatapoints={homeTeamBreakdownDatapoints}
                            awayTeamDatapoints={awayTeamBreakdownDatapoints}
                        ></BreakdownGraph>
                    </Col>
                </Row>
            </div>
        );
    };

    let title = (
        <>
            <span className="h1 mr-1">Review</span>
        </>
    );

    let tableTabContents = loading ? (
        <p>
            <em>Loading...</em>
        </p>
    ) : (
        <>{inputList && renderTable(inputList.inputList)}</>
    );
    let clipOverviewTabContents = loading ? (
        <p>
            <em>Loading...</em>
        </p>
    ) : (
        <>
            {clipOverview &&
                clipOverviewTable(clipOverview.inputList)}
        </>
    );
    let graphTableContents = loading ? (
        <p>
            <em>Loading...</em>
        </p>
    ) : (
        <>{supremacyChart && renderGraph(supremacyChart.supremacyChart)}</>
    );

    let overviewContents = loading ? (
        <p>
            <em>Loading...</em>
        </p>
    ) : (
        <>{breakdown && renderBreakdown(breakdown)}</>
    );

    return (
        <div>
            <Row className="mr-0">
                <Col>{title}</Col>
                <Col className="">
                    {reviewDetails && (
                        <a
                            className="btn btn-outline-primary btn-sm float-right ml-2 my-1"
                            href={`/api/fixturerooms/${reviewDetails.fixtureRoomId}/scores/export`}
                            download
                        >
                            Export Scores
                        </a>
                    )}

                    {reviewDetails && isRoomActive ? <Button size="sm"
                                                             variant="outline-danger"
                                                             className="ml-2 float-right my-1"
                                                             onClick={async (clickInfo) => {
                                                                 await liveRoomService.endRoom(reviewDetails.fixtureRoomId, activeUserId);
                                                                 await setIsRoomActive((current) => !current);
                                                             }}>End Room</Button>
                        : reviewDetails && !isRoomActive && userRole === ApplicationRoles.Manager ?
                            <Button size="sm"
                                    variant="outline-danger"
                                    className="ml-2 float-right my-1"
                                    onClick={async (clickInfo) => {
                                        await liveRoomService.reopenRoom(reviewDetails.fixtureRoomId);
                                        await setIsRoomActive((current) => !current);
                                    }}>Reopen Room</Button> : <></>
                     }

                    {(reviewDetails && !isRoomActive) && (
                        <Button
                            className="ml-2 float-right my-1"
                            variant="outline-primary"
                            size="sm"
                            onClick={async (e) => {
                                await liveRoomService.syncRoomScore(reviewDetails.fixtureRoomId);
                                toast.success("Data sent successfully. It will take a few moments to appear on Taka.");
                            }}
                        >
                            Re-Sync Match Score to Taka
                        </Button>
                    )}


                    {reviewDetails && (
                        <Link
                            to={`/scoring/${reviewDetails.fixtureRoomId}`}
                            href="#"
                            className="float-right ml-2 my-1 btn btn-info btn-sm"
                            style={{cursor: "pointer"}}
                        >
                            Back to room
                        </Link>
                    )}
                </Col>
            </Row>

            <Tabs defaultActiveKey="overview" id="tabs" className="mb-3">
                <Tab eventKey="overview" title="Overview">
                    {overviewContents}
                </Tab>
                <Tab eventKey="inputs" title="Inputs">
                    {tableTabContents}
                </Tab>
                <Tab eventKey="supremacy" title="Supremacy">
                    {graphTableContents}
                </Tab>
                {/*<Tab eventKey="clipOverview" title="Clip Overview">*/}
                {/*  {clipOverviewTabContents}*/}
                {/*</Tab>*/}
            </Tabs>
            {reviewNoteModal}
        </div>
    );
};

const getSupremacyPoint = (
    type,
    clipStartSupremacy,
    clipEndSupremacy,
    actionSupremacy
) => {
    switch (type) {
        case 3:
            return clipStartSupremacy
                ? Math.round(clipStartSupremacy * 1000) / 1000
                : "";
        case 4:
            return clipEndSupremacy ? Math.round(clipEndSupremacy * 1000) / 1000 : "";
        case 5:
            return actionSupremacy ? Math.round(actionSupremacy * 1000) / 1000 : "";
        case 11:
            return actionSupremacy ? Math.round(actionSupremacy * 1000) / 1000 : "";
        case 13:
            return actionSupremacy ? Math.round(actionSupremacy * 1000) / 1000 : "";
        default:
            return "";
    }
};

Review.displayName = Review.name;

export default Review;
